'use client'

import { useEffect, useState } from 'react'

/**
 * Shared key with the pre-hydration inline script in layout.tsx.
 * Both handlers use the same key so they don't double-reload.
 */
const CHUNK_RELOAD_KEY = 'caxe-prehyd-chunk'

function isChunkLoadError(event: ErrorEvent | PromiseRejectionEvent): boolean {
    const err = event instanceof ErrorEvent ? event.error : event.reason
    if (!err) return false
    const name = err?.name ?? ''
    const message = typeof err?.message === 'string' ? err.message : ''
    return name === 'ChunkLoadError' || /Loading chunk [^\s]+ failed/i.test(message)
}

/**
 * Catches ChunkLoadError (e.g. after a new deploy when the browser still has old chunk URLs).
 *
 * Two-phase recovery:
 *  1. Pre-hydration (inline script in <body>): auto-reloads once, then shows static overlay.
 *  2. Post-hydration (this component): if we're still seeing the error after React mounts,
 *     show the full-styled overlay with reload button.
 *
 * On successful mount we clear the reload flag so auto-recovery works for the next deploy.
 */
export function ChunkLoadErrorHandler() {
    const [showRecovery, setShowRecovery] = useState(false)

    useEffect(() => {
        // Page loaded successfully — clear the flag so the next chunk-error gets auto-reloaded.
        try { sessionStorage.removeItem(CHUNK_RELOAD_KEY) } catch (_) {}

        const handleError = (event: ErrorEvent) => {
            if (!isChunkLoadError(event)) return
            event.preventDefault()
            const attempted = sessionStorage.getItem(CHUNK_RELOAD_KEY)
            if (attempted === '1') {
                setShowRecovery(true)
                return
            }
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
            window.location.reload()
        }

        const handleRejected = (event: PromiseRejectionEvent) => {
            if (!isChunkLoadError(event)) return
            event.preventDefault()
            const attempted = sessionStorage.getItem(CHUNK_RELOAD_KEY)
            if (attempted === '1') {
                setShowRecovery(true)
                return
            }
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
            window.location.reload()
        }

        window.addEventListener('error', handleError)
        window.addEventListener('unhandledrejection', handleRejected)
        return () => {
            window.removeEventListener('error', handleError)
            window.removeEventListener('unhandledrejection', handleRejected)
        }
    }, [])

    const handleReload = () => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY)
        window.location.reload()
    }

    if (!showRecovery) return null

    return (
        <div
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-100 px-4"
            role="alert"
            aria-live="assertive"
        >
            <div className="max-w-md rounded-2xl bg-white p-8 shadow-xl text-center">
                <h1 className="text-lg font-semibold text-gray-900">Update available</h1>
                <p className="mt-2 text-sm text-gray-600">
                    A new version of CloudAxe is available. Please reload the page to continue.
                </p>
                <button
                    type="button"
                    onClick={handleReload}
                    className="mt-6 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-500"
                >
                    Reload page
                </button>
            </div>
        </div>
    )
}
